import { useRef, useState } from "react";
import { useCanAccess, useGetList, useTranslate } from "ra-core";

import { Skeleton } from "@/components/ui/skeleton";
import { ProgressBar } from "../design/ProgressBar";
import { StatusPill } from "../design/StatusPill";
import { ListErrorState } from "../design/ListStates";
import {
  CreditTypeGroup,
  LookingForGroup,
  TimingGroup,
  WhoGroup,
  type DealProfileGroupsCatalogues,
} from "./DealProfileGroups";
import {
  ProfileBudgetFields,
  ProfileSingleAmountField,
} from "./ProfileAmountFields";
import { ProfileNotesField } from "./ProfileTextFields";
import {
  countProfileDimensions,
  PROFILE_DIMENSIONS_TOTAL,
} from "./profileRules";
import {
  useDealProfileSave,
  type DealProfileSaveStatus,
} from "./useDealProfileSave";
import type { Deal, DealProfile, ProfileOption, Project } from "../types";

const CATALOGUE_PAGINATION = { page: 1, perPage: 1000 } as const;
const CATALOGUE_SORT = { field: "position", order: "ASC" as const };

export interface DealProfileSectionProps {
  /**
   * Almikia Epic EF (TASK-119): the only structural requirement (per the
   * ticket) -- no dependency on the lead show dialog's layout or record
   * context, so the Hoy sheet (TASK-122) mounts this unchanged on mobile.
   */
  deal: Deal;
  className?: string;
}

/**
 * Almikia Epic EF (TASK-119), vision #5: "Construir el perfil durante el
 * Discovery con opciones rápidas + notas libres" -- the lead's 'Perfil del
 * cliente', mostly captured by tapping chips with every tap saved at once
 * and a visible autosave status (design-system.md section 2 principle 2).
 * Expanded by default while the lead is in Discovery, collapsed (header
 * only, tappable) at every other stage.
 */
export function DealProfileSection({
  deal,
  className,
}: DealProfileSectionProps) {
  const translate = useTranslate();
  const [isExpanded, setIsExpanded] = useState(deal.stage === "discovery");
  // `useState`'s initializer only runs on first mount, so a caller that
  // reuses the same mounted `DealProfileSection` for a different lead (e.g.
  // switching leads inside one show dialog) would otherwise keep whichever
  // lead's Discovery-default it first mounted with. Recompute the default
  // explicitly the moment `deal.id` changes, mirroring the "expanded by
  // default in Discovery" rule for the new lead.
  const dealIdRef = useRef(deal.id);
  if (dealIdRef.current !== deal.id) {
    dealIdRef.current = deal.id;
    setIsExpanded(deal.stage === "discovery");
  }

  const { canAccess: canWrite, isPending: isAccessPending } = useCanAccess({
    resource: "deal_profiles",
    action: "edit",
    record: deal,
  });

  const {
    profile,
    isPending: isProfilePending,
    error: profileError,
    refetch: refetchProfile,
    saveStatus,
    errorMessage,
    saveField,
  } = useDealProfileSave(deal.id);

  const {
    data: buyerTypes = [],
    isPending: buyerTypesPending,
    error: buyerTypesError,
    refetch: refetchBuyerTypes,
  } = useGetList<ProfileOption>("profile_buyer_types", {
    filter: {},
    pagination: CATALOGUE_PAGINATION,
    sort: CATALOGUE_SORT,
  });
  const {
    data: creditTypes = [],
    isPending: creditTypesPending,
    error: creditTypesError,
    refetch: refetchCreditTypes,
  } = useGetList<ProfileOption>("profile_credit_types", {
    filter: {},
    pagination: CATALOGUE_PAGINATION,
    sort: CATALOGUE_SORT,
  });
  const {
    data: timelines = [],
    isPending: timelinesPending,
    error: timelinesError,
    refetch: refetchTimelines,
  } = useGetList<ProfileOption>("profile_timelines", {
    filter: {},
    pagination: CATALOGUE_PAGINATION,
    sort: CATALOGUE_SORT,
  });
  const {
    data: motivators = [],
    isPending: motivatorsPending,
    error: motivatorsError,
    refetch: refetchMotivators,
  } = useGetList<ProfileOption>("profile_motivators", {
    filter: {},
    pagination: CATALOGUE_PAGINATION,
    sort: CATALOGUE_SORT,
  });
  const {
    data: objections = [],
    isPending: objectionsPending,
    error: objectionsError,
    refetch: refetchObjections,
  } = useGetList<ProfileOption>("profile_objections", {
    filter: {},
    pagination: CATALOGUE_PAGINATION,
    sort: CATALOGUE_SORT,
  });
  const {
    data: projects = [],
    isPending: projectsPending,
    error: projectsError,
    refetch: refetchProjects,
  } = useGetList<Project>("projects", {
    filter: {},
    pagination: CATALOGUE_PAGINATION,
    sort: { field: "name", order: "ASC" },
  });

  const isLoading =
    isAccessPending ||
    isProfilePending ||
    buyerTypesPending ||
    creditTypesPending ||
    timelinesPending ||
    motivatorsPending ||
    objectionsPending ||
    projectsPending;
  const loadError =
    profileError ||
    buyerTypesError ||
    creditTypesError ||
    timelinesError ||
    motivatorsError ||
    objectionsError ||
    projectsError;

  const retry = () => {
    refetchProfile();
    refetchBuyerTypes();
    refetchCreditTypes();
    refetchTimelines();
    refetchMotivators();
    refetchObjections();
    refetchProjects();
  };

  const count = countProfileDimensions(profile, deal);
  const title = translate("resources.deals.deal_profile.title", {
    _: "Perfil del cliente",
  });

  return (
    <div data-testid="deal-profile-section" className={className}>
      <button
        type="button"
        data-testid="deal-profile-toggle"
        onClick={() => setIsExpanded((current) => !current)}
        aria-expanded={isExpanded}
        className="flex w-full min-h-11 flex-col gap-2 py-2 text-left"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold">{title}</h2>
          <span
            data-testid="deal-profile-count"
            className="text-xs tabular-nums text-muted-foreground"
          >
            {translate("resources.deals.deal_profile.header_count", {
              count,
              total: PROFILE_DIMENSIONS_TOTAL,
              _: `${count} de ${PROFILE_DIMENSIONS_TOTAL} datos`,
            })}
          </span>
        </div>
        <ProgressBar
          value={count}
          max={PROFILE_DIMENSIONS_TOTAL}
          label={title}
        />
      </button>

      {isExpanded ? (
        isLoading ? (
          <div aria-busy="true" role="status" className="space-y-3 py-4">
            <span className="sr-only">{translate("crm.common.loading")}</span>
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : loadError ? (
          <ListErrorState
            message={translate("resources.deals.deal_profile.error_loading", {
              _: "No se pudo cargar el perfil",
            })}
            onRetry={retry}
          />
        ) : (
          <DealProfileBody
            deal={deal}
            profile={profile ?? {}}
            catalogues={{
              buyerTypes,
              creditTypes,
              timelines,
              motivators,
              objections,
              projects,
            }}
            canWrite={!!canWrite}
            saveField={saveField}
            saveStatus={saveStatus}
            errorMessage={errorMessage}
          />
        )
      ) : null}
    </div>
  );
}

interface DealProfileBodyProps {
  deal: Deal;
  profile: Partial<DealProfile>;
  catalogues: DealProfileGroupsCatalogues;
  canWrite: boolean;
  saveField: (patch: Partial<DealProfile>) => Promise<void>;
  saveStatus: DealProfileSaveStatus;
  errorMessage: string | null;
}

function DealProfileBody({
  deal,
  profile,
  catalogues,
  canWrite,
  saveField,
  saveStatus,
  errorMessage,
}: DealProfileBodyProps) {
  const translate = useTranslate();
  const groupProps = { deal, profile, catalogues, canWrite, saveField };

  return (
    <div className="flex flex-col gap-6 py-4">
      <SaveStatusIndicator
        status={saveStatus}
        errorMessage={errorMessage}
        translate={translate}
      />

      <WhoGroup {...groupProps} />
      <LookingForGroup {...groupProps} />

      <div className="flex flex-col gap-4">
        <h3 className="text-sm font-semibold">
          {translate("resources.deals.deal_profile.groups.payment", {
            _: "Cómo paga",
          })}
        </h3>
        <ProfileBudgetFields
          budgetMin={profile.budget_min ?? null}
          budgetMax={profile.budget_max ?? null}
          minLabel={translate(
            "resources.deals.deal_profile.fields.budget_min",
            {
              _: "Presupuesto desde",
            },
          )}
          maxLabel={translate(
            "resources.deals.deal_profile.fields.budget_max",
            {
              _: "Presupuesto hasta",
            },
          )}
          onSaveMin={(value) => saveField({ budget_min: value })}
          onSaveMax={(value) => saveField({ budget_max: value })}
          readOnly={!canWrite}
        />
        <CreditTypeGroup {...groupProps} />
        <ProfileSingleAmountField
          id="profile-down-payment"
          label={translate("resources.deals.deal_profile.fields.down_payment", {
            _: "Enganche",
          })}
          value={profile.down_payment ?? null}
          readOnly={!canWrite}
          onSave={(value) => saveField({ down_payment: value })}
        />
        <ProfileSingleAmountField
          id="profile-monthly-payment"
          label={translate(
            "resources.deals.deal_profile.fields.monthly_payment",
            { _: "Mensualidad" },
          )}
          value={profile.monthly_payment ?? null}
          readOnly={!canWrite}
          onSave={(value) => saveField({ monthly_payment: value })}
        />
      </div>

      <TimingGroup {...groupProps} />

      <div className="flex flex-col gap-4">
        <h3 className="text-sm font-semibold">
          {translate("resources.deals.deal_profile.fields.notes", {
            _: "Notas del perfil",
          })}
        </h3>
        <ProfileNotesField
          label={translate("resources.deals.deal_profile.fields.notes", {
            _: "Notas del perfil",
          })}
          value={profile.notes ?? null}
          readOnly={!canWrite}
          onSave={(value) => saveField({ notes: value })}
        />
      </div>
    </div>
  );
}

function SaveStatusIndicator({
  status,
  errorMessage,
  translate,
}: {
  status: DealProfileSaveStatus;
  errorMessage: string | null;
  translate: ReturnType<typeof useTranslate>;
}) {
  if (status === "idle") return null;

  if (status === "saving") {
    return (
      <p
        data-testid="deal-profile-save-status"
        role="status"
        className="text-xs text-muted-foreground"
      >
        {translate("resources.deals.deal_profile.status.saving", {
          _: "Guardando…",
        })}
      </p>
    );
  }

  if (status === "saved") {
    return (
      <p
        data-testid="deal-profile-save-status"
        role="status"
        className="text-xs text-muted-foreground"
      >
        {translate("resources.deals.deal_profile.status.saved", {
          _: "Guardado hace unos segundos",
        })}
      </p>
    );
  }

  return (
    <StatusPill
      tone="not-ok"
      text={
        errorMessage ??
        translate("resources.deals.deal_profile.status.error", {
          _: "No se pudo guardar. Intenta de nuevo.",
        })
      }
    />
  );
}
